import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'next-i18next';
import { Modal } from '@components/common/Modal/Modal';
import { useSwrAccountDiscountData } from '@hooks/useSwrApi/endpoints/order/useSwrAccountDiscountData';
import { PremiumDiscountBoxes } from '@components/features/cards/employer/PremiumDiscountBoxes';
import { CheckIcon } from '@heroicons/react/16/solid';
import { Button } from '@components/common';
import { useCompanyJobActions } from '@components/templates/my-jobs/hooks/useCompanyJobActions';
import React from 'react';


interface PremiumModalProps {
  locale: Locale;
  jobSlug: string;
  code: number;
  onPayPerViewCallback?: () => void;
  onSearchPackActivateCallback?: () => void;
}

/**
 * @description Renders the cv view limit exceeded modal
 *
 * The modal can be opened anywhere in the app using the **showPremiumModal** function.
 */
export const PremiumModal = NiceModal.create((props: PremiumModalProps) => {
  const {
    locale, code, onSearchPackActivateCallback,
  } = props;

  const modal = useModal();

  // Translation
  const { t } = useTranslation('common');


  const {
    data: accountDiscountData,
  } = useSwrAccountDiscountData({
    locale,
  });

  // Add job is allowed only for not logged or company users. We need this to check if we need to switch account
  // const headerInfo = useAppSelector((state) => state.user.headerInfo);

  const closeModal = () => {
    void modal.hide();
  };

  const {
    handleSearchpackConfirm,
    isActivatingSearchpack,
  } = useCompanyJobActions({
    jobSlug: '',
    currentJobState: 'active',
    onSuccesSearchpackActivation: () => {
      closeModal();
      onSearchPackActivateCallback?.();
    },
  });


  // Render component
  // *****************************************
  return (
    <Modal
      isOpen={modal.visible}
      onClose={closeModal}
      backdropClickDisabled
      size="lg"
    >
      <Modal.Header onClose={closeModal}>
        <h3>
          {code === 804
          && t('error.view-limit.missing-searchpack')}
          {code === 805
          && t('error.view-limit.subscription-quota')}
        </h3>
      </Modal.Header>
      <Modal.Body>
        <div className="justify-center md:flex">
          <div>
            <PremiumDiscountBoxes
              showOnlyNext
              currentDiscountPercentage={accountDiscountData?.currentDiscountPercentage}
              isAuth
            />
          </div>

          <div className="justify-between md:flex">
            <div className="m-5 text-center md:my-0 md:flex md:items-center md:text-left">{t('global.label.or.separator')}</div>

            <div>
              <div className="h-full rounded-md border p-4 md:flex md:flex-col md:justify-between">
                <h5 className="mb-3 text-md font-bold">
                  {t('my-jobs.card.actions.searchpack')}
                </h5>
                <div>

                  <p className="font-bold">
                    {' '}
                    {t('global.action.searchpack.details.included.benefits')}
                  </p>
                  <div className="my-5 flex items-center gap-5">
                    <div className="border-r-2 border-gray-500/20 pr-5">
                      <div className=" flex items-center gap-2">
                        <CheckIcon className="size-5 text-secondary" />
                        <span>{t('global.searchpack.benefit.2')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckIcon className="size-5 text-secondary" />
                        <span>{t('global.searchpack.benefit.1')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckIcon className="size-5 text-secondary" />
                        <span>{t('global.action.searchpack.details')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="font-bold">
                    200 neuro
                  </div>
                  <Button
                    color="secondary"
                    onClick={handleSearchpackConfirm}
                    isLoading={isActivatingSearchpack}
                    size="sm"
                    className="mt-5 rounded-full"
                  >
                    {t('global.action.searchpack.activate')}
                  </Button>
                </div>


              </div>
            </div>
          </div>
        </div>

      </Modal.Body>
    </Modal>
  );
});


/**
 * @description Opens the cv view limit modal
 *
 * @example
 * // import the function
 * import { showPremiumModal } from '@components/features/modals/employer/CvViewLimitExceeded';
 * // usage
 * <Button onClick={showPremiumModal()}>Show modal</Button>
 */
export const showPremiumModal = (
  locale: Locale,
  jobSlug: string,
  code: number,
  onPayPerViewCallback?: () => void,
  onSearchPackActivateCallback?: () => void,
) => {
  void NiceModal.show(PremiumModal, {
    locale,
    jobSlug,
    code,
    onPayPerViewCallback,
    onSearchPackActivateCallback,
  });
};
